'use client';

import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from './status-badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Square, AlertCircle } from 'lucide-react';
import { getRun, getRunHistory, stopRun, type RunMeta, type HistoryEntry } from '@/app/actions/runs';
import { toast } from 'sonner';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(start: string, end?: string): string {
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  const diffMs = endDate.getTime() - startDate.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffSec < 60) return `${diffSec}s`;
  if (diffMin < 60) return `${diffMin}m ${diffSec % 60}s`;
  return `${diffHour}h ${diffMin % 60}m`;
}

interface MetaItemProps {
  label: string;
  value: string | React.ReactNode;
}

function MetaItem({ label, value }: MetaItemProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

interface HistoryLogProps {
  entries: HistoryEntry[];
}

function HistoryLog({ entries }: HistoryLogProps) {
  if (entries.length === 0) {
    return (
      <div className="text-muted-foreground text-center py-4">
        No history entries
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {entries.map((entry, index) => (
        <div
          key={index}
          className="text-xs font-mono p-2 rounded bg-muted/50 overflow-x-auto"
        >
          <span className="text-muted-foreground">
            [{new Date(entry.ts).toLocaleTimeString()}]
          </span>{' '}
          <span className="text-blue-500">{entry.type}</span>
          {entry.content && (
            <span className="text-foreground">: {entry.content.slice(0, 200)}{entry.content.length > 200 ? '...' : ''}</span>
          )}
          {entry.tool && (
            <span className="text-green-500"> tool={entry.tool}</span>
          )}
          {entry.error && (
            <span className="text-red-500"> error={entry.error}</span>
          )}
        </div>
      ))}
    </div>
  );
}

interface RunDetailsSheetProps {
  runId: string | null;
  open: boolean;
  onClose: () => void;
}

export function RunDetailsSheet({ runId, open, onClose }: RunDetailsSheetProps) {
  const [run, setRun] = useState<RunMeta | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  useEffect(() => {
    if (runId && open) {
      loadRunDetails(runId);
    } else {
      setRun(null);
      setHistory([]);
    }
  }, [runId, open]);

  // SSE for real-time history updates via Next.js proxy
  useEffect(() => {
    if (!runId || !open || !run || run.status !== 'running') {
      return;
    }

    const eventSource = new EventSource(`/api/events/runs/${runId}`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'run:entry') {
          setHistory((prev) => [...prev, data.entry]);
        } else if (data.type === 'run:status') {
          setRun((prev) => prev ? { ...prev, status: data.status } : prev);
        } else if (data.type === 'run:completed') {
          setRun((prev) =>
            prev
              ? {
                  ...prev,
                  status: data.status,
                  usage: data.usage || prev.usage,
                  error: data.error,
                }
              : prev
          );
        }
      } catch (err) {
        console.error('[RunDetailsSheet] Failed to parse SSE event:', err);
      }
    };

    return () => {
      eventSource.close();
    };
  // Bug #10: Include `run` in dependency array to avoid stale closure
  }, [runId, open, run]);

  const loadRunDetails = async (id: string) => {
    setIsLoading(true);
    try {
      const [runData, historyData] = await Promise.all([
        getRun(id),
        getRunHistory(id),
      ]);
      setRun(runData);
      setHistory(historyData);
    } catch (err) {
      console.error('Failed to load run details:', err);
      toast.error('Failed to load run details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStop = async () => {
    if (!runId) return;

    setIsStopping(true);
    try {
      const result = await stopRun(runId);
      if (result.success) {
        toast.success('Run stopped');
        setRun((prev) => (prev ? { ...prev, status: 'stopped' } : prev));
      } else {
        toast.error(`Failed to stop run: ${result.error}`);
      }
    } finally {
      setIsStopping(false);
    }
  };

  const isActive = run?.status === 'running' || run?.status === 'starting';

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-hidden flex flex-col">
        <SheetHeader className="shrink-0">
          <SheetTitle className="flex items-center gap-2">
            {run?.agent || 'Run Details'}
            {run && <StatusBadge status={run.status} />}
          </SheetTitle>
          <SheetDescription>
            {runId ? `Run ID: ${runId}` : 'Loading...'}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-muted-foreground">Loading...</span>
          </div>
        ) : run ? (
          <div className="flex-1 flex flex-col gap-4 overflow-hidden mt-4">
            {/* Meta info */}
            <Card className="shrink-0">
              <CardContent className="grid grid-cols-2 gap-4 p-4">
                <MetaItem
                  label="Trigger"
                  value={run.trigger.type}
                />
                <MetaItem label="Model" value={run.model} />
                <MetaItem
                  label="Duration"
                  value={formatDuration(run.created_at, run.completed_at)}
                />
                <MetaItem
                  label="Cost"
                  value={
                    run.usage?.totalCost
                      ? `$${run.usage.totalCost.toFixed(4)}`
                      : '-'
                  }
                />
                <MetaItem label="Started" value={formatDate(run.created_at)} />
                {run.completed_at && (
                  <MetaItem label="Completed" value={formatDate(run.completed_at)} />
                )}
              </CardContent>
            </Card>

            {/* Error if any */}
            {run.error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 shrink-0">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <span className="text-sm">{run.error}</span>
              </div>
            )}

            {/* Stop button */}
            {isActive && (
              <Button
                variant="destructive"
                onClick={handleStop}
                disabled={isStopping}
                className="shrink-0"
              >
                <Square className="h-4 w-4 mr-2" />
                {isStopping ? 'Stopping...' : 'Stop Run'}
              </Button>
            )}

            {/* History log */}
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              <h3 className="font-semibold mb-2 shrink-0">Execution Log</h3>
              <ScrollArea className="flex-1">
                <HistoryLog entries={history} />
              </ScrollArea>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-muted-foreground">Run not found</span>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
